import { IssuerEdit } from './../model/issuerEdit';
import { Component, OnInit } from '@angular/core';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { IssuerService } from 'src/app/core/service/issuer.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, GroupDescriptor, filterBy } from '@progress/kendo-data-query';

@Component({
  standalone: false,
  selector: 'app-issuer-list',
  templateUrl: './issuer-list.component.html',
  styleUrls: ['./issuer-list.component.scss']
})
export class IssuerListComponent implements OnInit
{
  public pageSizes = [5, 10, 25, 50, 100];
  public view: GridViewManager;
  public deleteDialogOpened = false;
  public enableAdvanced = false;
  public count: number;
  public isNew = false;
  public editDataItem: IssuerEdit;
  public selectedIssuer: IssuerEdit;
  public filterText = '';

  constructor(private issuerService: IssuerService,
    private messageHandlerService: MessageHandlerService,
    private appConfigService: AppConfigService,
    private searchHelperService: SearchHelperService)
  {
  }

  public ngOnInit(): void
  {
    this.view = new GridViewManager(this.appConfigService.getConfig().pageSize);
    this.loadGridData();
  }

  /**
   * Closes a component
   *
   * @param component Name of component to be closed.
   */
  public close(component: string): void
  {
    this[component + 'Opened'] = false;
  }

  /**
   * Opens a component
   *
   * @param component Name of component to be opened.
   */
  public open(component: string): void
  {
    this[component + 'Opened'] = true;
  }

  /**
   * Button action handler.
   *
   * @param confirm Confirm the action: yes/no
   */
  public deleteAction(confirm: boolean): void
  {
    if (confirm)
    {
      // Proceed with the deletion
      const issuerId = this.selectedIssuer.id;
      this.issuerService.deleteIssuer(issuerId).subscribe({
        next: () =>
        {
          this.view.data = this.view.data.filter(e => e.id !== issuerId);
          this.view.refreshView();
          this.count = this.view.data.length;
          this.messageHandlerService.displaySuccess('ISSUER-DELETED-SUCCESSFULLY');
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    // post-deletion cleanup and refresh grid
    this.close('deleteDialog');
    this.selectedIssuer = null;
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.view.setState(state);
  }

  /**
   * Group change event handler.
   *
   * @param groups List of GroupDescriptors
   */
  public groupChange(groups: GroupDescriptor[]): void
  {
    this.view.setGroup(groups);
  }

  /**
   * Sets the user to delete, and opens the delete user dialog.
   */
  public deleteHandler(id: number): void
  {
    // Check if in use
    this.issuerService.existsIssuer(id).subscribe({
      next: (inUseResponse) =>
      {
        // If in use, avoid deletion
        if (inUseResponse)
        {
          this.messageHandlerService.displayWarning('ISSUER-IN-USE-CANNOT-DELETE');
        }
        else
        {
          this.issuerService.getIssuerById(id).subscribe({
            next: (data) =>
            {
              this.selectedIssuer = data as IssuerEdit;
              this.open('deleteDialog');
            },
            error: (errorResponse) =>
            {
              this.messageHandlerService.displayError(errorResponse);
            }
          });
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public addHandler(): void
  {
    this.isNew = true;
    this.editDataItem = new IssuerEdit();
  }

  public editHandler(id: number): void
  {
    this.isNew = false;
    this.issuerService.getIssuerById(id).subscribe({
      next: (data) =>
      {
        this.editDataItem = data;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public saveChangesHandler(issuer: IssuerEdit): void
  {
    this.issuerService.saveIssuer(issuer, this.isNew, issuer.id).subscribe({
      next: (data) =>
      {
        // Must convert the lastModifiedOn into Date object
        data.lastModifiedOn = new Date(data.lastModifiedOn);
        this.view.addOrUpdateGridRow(data);
        this.count = this.view.data.length;  // Update count

        this.editDataItem = undefined;
        this.messageHandlerService.displaySuccess('ISSUER-SAVED-SUCCESSFULLY');
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public cancelChangesHandler(): void
  {
    this.editDataItem = undefined;
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.view.state.filter = filter;
    this.view.gridData.data = filterBy(this.view.data, filter);
    this.count = this.view.gridData.data.length;
    this.view.refreshView();
  }

  public searchFieldChange(filterText: string): void
  {
    this.view.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.view.state);
    this.filterText = filterText;
    this.filterChange(root);
  }

  private loadGridData(): void
  {
    this.view.startLoading();
    this.issuerService.getIssuerList().subscribe({
      next: (data) =>
      {
        data.forEach(d => { d.lastModifiedOn = new Date(d.lastModifiedOn); });
        this.count = data.length;
        this.view.setData(data);
        this.view.setSort([{ field: 'name', dir: 'asc' }]);
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      },
      complete: () =>
      {
        this.view.stopLoading();
      }
    });
  }
}
